import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { collection, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { auth, db, storage } from '../firebase';

export default function Inbox({ user }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy]   = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'intake'), snap => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      all.sort((a, b) => {
        const at = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
        const bt = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
        return bt - at;
      });
      setItems(all);
    }, err => console.error('Intake query error:', err));
    return unsub;
  }, []);

  const fmt = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const markHandled = async (it) => {
    setBusy(it.id);
    try { await updateDoc(doc(db, 'intake', it.id), { status: it.status === 'handled' ? 'new' : 'handled' }); }
    finally { setBusy(null); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete this submission from ${it.senderName}? Files will be removed too.`)) return;
    setBusy(it.id);
    try {
      await Promise.all((it.files || []).map(async (f) => {
        if (!f?.path) return;
        try { await deleteObject(ref(storage, f.path)); }
        catch (e) { if (e.code !== 'storage/object-not-found') throw e; }
      }));
      await deleteDoc(doc(db, 'intake', it.id));
    } catch (e) { alert(`Could not delete: ${e.message}`); }
    finally { setBusy(null); }
  };

  const newCount = items.filter(i => i.status !== 'handled').length;

  return (
    <>
      <nav className="navbar">
        <div className="nav-logo">
          <img src="/logo.png" alt="ARCHX AI" style={{ height: '100px', width: 'auto' }} />
        </div>
        <div className="nav-right">
          <span className="nav-user">{user.email}</span>
          <button className="nav-signout" onClick={() => signOut(auth)}>Sign out</button>
        </div>
      </nav>

      <div className="page">
        <div className="card">
          <div className="card-header">
            <span className="card-title">Incoming files {newCount > 0 ? `· ${newCount} new` : ''}</span>
            <button className="btn-gold" onClick={() => navigate('/dashboard')}>← Dashboard</button>
          </div>

          {items.length === 0 ? (
            <div className="empty-state">
              No submissions yet. Share your link <strong>/send</strong> (plus the access code) with dentists and labs.
            </div>
          ) : (
            items.map(it => (
              <div key={it.id} style={{
                border: '1px solid #eee', borderRadius: '12px', padding: '16px',
                marginBottom: '12px', background: it.status === 'handled' ? '#fafafa' : 'white',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '15px', color: '#111' }}>
                      {it.senderName} {it.practice ? <span style={{ color: '#888', fontWeight: 400 }}>· {it.practice}</span> : null}
                    </div>
                    <div style={{ fontSize: '13px', color: '#666', marginTop: '2px' }}>
                      {it.senderEmail} · {fmt(it.createdAt)}
                      {it.caseRef ? <> · <strong>Case:</strong> {it.caseRef}</> : null}
                    </div>
                  </div>
                  <span style={{
                    padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
                    color: it.status === 'handled' ? '#16a34a' : '#c8922a',
                    background: it.status === 'handled' ? '#f0fdf4' : '#fffbf0', whiteSpace: 'nowrap', height: 'fit-content',
                  }}>{it.status === 'handled' ? 'Handled' : 'New'}</span>
                </div>

                {it.notes ? <div style={{ fontSize: '13px', color: '#444', marginTop: '10px', fontStyle: 'italic' }}>“{it.notes}”</div> : null}

                <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {(it.files || []).map((f, i) => (
                    <a key={i} href={f.url || '#'} target="_blank" rel="noreferrer"
                      style={{ fontSize: '12px', color: '#1d4ed8', textDecoration: 'none', background: '#eff6ff',
                        border: '1px solid #dbeafe', borderRadius: '8px', padding: '6px 10px' }}>
                      ↓ {f.name}
                    </a>
                  ))}
                  {(!it.files || it.files.length === 0) && <span style={{ fontSize: '12px', color: '#aaa' }}>No files</span>}
                </div>

                <div style={{ marginTop: '14px', display: 'flex', gap: '8px' }}>
                  <button onClick={() => markHandled(it)} disabled={busy === it.id}
                    style={{ padding: '6px 14px', borderRadius: '8px', border: '1px solid #d1d5db', background: 'white',
                      color: '#374151', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                    {it.status === 'handled' ? 'Mark new' : 'Mark handled'}
                  </button>
                  <button onClick={() => remove(it)} disabled={busy === it.id}
                    style={{ padding: '6px 14px', borderRadius: '8px', border: '1px solid #fecaca', background: 'white',
                      color: '#dc2626', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}
